/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import picturefill from 'picturefill';
import objectFitImages from 'object-fit-images';
import classnames from 'classnames';
import './Person.scss';

/**
 * This is the Person component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Person({
  className,
  businesses,
  url,
  image,
  cv,
  name,
  role,
  contacts,
  business: businessLabel,
  style,
}) {
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      picturefill({
        elements: [imageRef.current],
      });
      objectFitImages(imageRef.current);
    }
  }, []);

  function renderBusinessAreas() {
    if (!businesses.length) return null;

    return (
      <div className="businesses">
        <strong>{businessLabel}</strong>
        <div>
          {businesses.map(({ id, name, link }, i) => {
            const comma = i < businesses.length - 1 ? ',' : null;
            if (link) {
              return (
                <React.Fragment key={id}>
                  <a href={link}>
                    {name}
                    {comma}
                  </a>
                  &nbsp;
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={id}>
                <span key={id}>
                  {name}
                  {comma}
                </span>
                &nbsp;
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }
  const business = businesses.length ? businesses[0].color : 'undefined';

  return (
    <div
      className={classnames(`person business-${business}`, className)}
      style={style}
      role="group"
      aria-label={name}
    >
      <a className="image" href={url}>
        <div>
          {image ? <img ref={imageRef} {...image} /> : null}
          <span className="cv">{cv}</span>
        </div>
      </a>
      <div className="text">
        <h4>{name}</h4>
        <div className="role" dangerouslySetInnerHTML={{ __html: role }} />
        <div className="methods">
          {contacts.map(({ label, type, value }, i) => (
            <div key={i}>
              <strong>{label}</strong>
              {(() => {
                switch (type) {
                  case 'tel':
                    return <a href={`tel: ${value}`}>{value}</a>;
                  case 'email':
                    return <a href={`mailto: ${value}`}>{value}</a>;
                  default:
                    return <a href={value}>{value}</a>;
                }
              })()}
            </div>
          ))}
          {renderBusinessAreas()}
        </div>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Person.propTypes = {
  className: PropTypes.string,
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  businesses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      link: PropTypes.string,
    })
  ).isRequired,
  image: PropTypes.object,
  cv: PropTypes.string.isRequired,
  business: PropTypes.string.isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  style: PropTypes.object,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Person.defaultProps = {
  businesses: [],
  image: null,
  cv: 'Show CV',
  business: 'Business areas',
  contacts: [],
};

export default Person;
